import { useEffect, useMemo, useRef, useState } from "react"
import useMidiMapping from "../hooks/useMidiMapping"
import MidiAssignMenu from "./MidiAssignMenu"
import { useStorePads } from "../store"

export default function InputRange({
    label,
    showLabel = true,
    showValue = true,
    className,
    value = 0,
    templateValue,
    onChange,
    min = 0,
    max = 1,
    step = 0.01,
    unit = '%',
    midiTargetId,
    onMidiControl,
    onMidiAssigned
}) {
    const [assignedMapping, setAssignedMapping] = useState(null)
    const [isMapping, setIsMapping] = useState(false)
    const [isContextVisible, setIsContextVisible] = useState(false)
    const [contextPosition, setContextPosition] = useState({ x: 0, y: 0 })
    const [selectedDeviceId, setSelectedDeviceId] = useState('')
    const wrapperRef = useRef(null)
    const onChangeRef = useRef(onChange)
    const onMidiControlRef = useRef(onMidiControl)
    const onMidiAssignedRef = useRef(onMidiAssigned)
    const minRef = useRef(Number(min))
    const maxRef = useRef(Number(max))
    const longPressTimerRef = useRef(null)
    const longPressTriggeredRef = useRef(false)
    const touchStartRef = useRef({ x: 0, y: 0 })
    const removeMidiAssignment = useStorePads((state) => state.removeMidiAssignment)
    const {
        initMidi,
        isMidiReady,
        midiInputs,
        selectedInputId,
        selectMidiDevice,
        registerMidiTarget,
        unregisterMidiTarget,
        startMidiLearning,
        stopMidiLearning,
        getNoteName,
        getAssignedMidiAssignment,
        applyMidiAssignmentToTarget
    } = useMidiMapping()

    const targetId = useMemo(() => midiTargetId || `input-range-${label}`.replace(/\s+/g, '-').toLowerCase(), [midiTargetId, label])

    useEffect(() => {
        initMidi()
    }, [initMidi])

    useEffect(() => {
        onChangeRef.current = onChange
        onMidiControlRef.current = onMidiControl
        onMidiAssignedRef.current = onMidiAssigned
        minRef.current = Number(min)
        maxRef.current = Number(max)
    }, [onChange, onMidiControl, onMidiAssigned, min, max])

    useEffect(() => {
        if (!targetId) return
        const existingAssignment = getAssignedMidiAssignment(targetId)
        if (existingAssignment != null) {
            setAssignedMapping(existingAssignment)
        }
    }, [targetId, getAssignedMidiAssignment])

    useEffect(() => {
        if (selectedInputId) {
            setSelectedDeviceId(selectedInputId)
        }
    }, [selectedInputId])

    useEffect(() => {
        if (!targetId) return

        const handleMidiMessage = (message) => {
            const rawValue = message?.value ?? 0
            if (typeof onMidiControlRef.current === 'function') {
                onMidiControlRef.current(message)
                return
            }
            const normalized = Math.max(0, Math.min(1, rawValue / 127))
            const scaledValue = minRef.current + normalized * (maxRef.current - minRef.current)
            onChangeRef.current({ target: { value: scaledValue } })
        }

        const handleMidiAssigned = (assignment) => {
            setAssignedMapping(assignment)
            setIsMapping(false)
            onMidiAssignedRef.current?.(assignment)
        }

        registerMidiTarget(targetId, {
            onMidiMessage: handleMidiMessage,
            onMidiAssigned: handleMidiAssigned
        })

        const persistedAssignment = getAssignedMidiAssignment(targetId)
        if (persistedAssignment) {
            applyMidiAssignmentToTarget(targetId, persistedAssignment)
        }

        return () => unregisterMidiTarget(targetId)
    }, [targetId, registerMidiTarget, unregisterMidiTarget, getAssignedMidiAssignment, applyMidiAssignmentToTarget])

    useEffect(() => {
        if (isMapping && targetId) {
            startMidiLearning(targetId)
            return () => stopMidiLearning()
        }

        stopMidiLearning()
    }, [isMapping, targetId, startMidiLearning, stopMidiLearning])

    useEffect(() => {
        const onClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsContextVisible(false)
            }
        }
        window.addEventListener('click', onClickOutside)
        return () => {
            window.removeEventListener('click', onClickOutside)
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current)
            }
        }
    }, [])

    const openContextMenu = (x, y) => {
        setContextPosition({ x, y })
        setIsContextVisible(true)
    }

    const handleContextMenu = (event) => {
        event.preventDefault()
        openContextMenu(event.clientX, event.clientY)
    }

    const clearLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current)
            longPressTimerRef.current = null
        }
    }

    const handleTouchStart = (event) => {
        const touch = event.touches?.[0]
        if (!touch) return

        longPressTriggeredRef.current = false
        touchStartRef.current = { x: touch.clientX, y: touch.clientY }
        clearLongPress()
        longPressTimerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true
            openContextMenu(touch.clientX, touch.clientY)
        }, 550)
    }

    const handleTouchMove = (event) => {
        const touch = event.touches?.[0]
        if (!touch) return

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x)
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y)
        if (deltaX > 10 || deltaY > 10) {
            clearLongPress()
        }
    }

    const handleTouchEnd = (event) => {
        if (longPressTriggeredRef.current) {
            event.preventDefault()
        }
        clearLongPress()
    }

    const toggleMapping = () => {
        if (!isMidiReady) return
        setIsMapping((current) => !current)
    }

    const handleDeviceChange = (event) => {
        const id = event.target.value
        setSelectedDeviceId(id)
        selectMidiDevice(id)
    }

    const handleClearMapping = () => {
        setAssignedMapping(null)
        removeMidiAssignment(targetId)
        applyMidiAssignmentToTarget(targetId, null)
        setIsMapping(false)
        setIsContextVisible(false)
        onMidiAssignedRef.current?.(null)
    }

    return (
        <div
            ref={wrapperRef}
            className="relative"
            onContextMenu={handleContextMenu}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={clearLongPress}
        >
            <div className={`relative grid ${showLabel ? 'grid-cols-[30px_auto_40px]' : 'grid-cols-[auto]'} ${showValue ? 'grid-rows-[auto_auto]' : 'grid-rows-[auto]'} items-center gap-2 text-sm text-slate-200`}>
                {showLabel && (
                    <label htmlFor={label} className="uppercase tracking-wider">{label}</label>
                )}
                <input
                    type="range"
                    className={`${className} m-0 p-0 accent-gray-500`}
                    value={value}
                    onChange={(e) => onChange(e)}
                    min={min}
                    max={max}
                    step={step}
                />
                {showValue && (
                    <span className="absolute right-0 -top-4 w-14 text-sm">{parseFloat(templateValue ?? value).toFixed(0)}{unit}</span>
                )}
                {assignedMapping != null && (
                    <span className="absolute left-0 -top-4 text-[7px] bg-black text-indigo-400 font-semibold px-1 rounded-sm">
                        {assignedMapping.type === 'cc'
                            ? `CC#${assignedMapping.number}`
                            : getNoteName(assignedMapping.number)}
                    </span>
                )}
            </div>
            <MidiAssignMenu
                isVisible={isContextVisible}
                contextPosition={contextPosition}
                isMapping={isMapping}
                toggleLabel="Asignar control MIDI"
                selectedDeviceId={selectedDeviceId}
                midiInputs={midiInputs}
                onToggleMapping={toggleMapping}
                onDeviceChange={handleDeviceChange}
                onClearMapping={handleClearMapping}
            />
        </div>
    )
}
